import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import CircularProgress from "@mui/material/CircularProgress";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import BuildIcon from "@mui/icons-material/Build";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import LoopIcon from "@mui/icons-material/Loop";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import { WorkflowRunDetails } from "./Workflows/WorkflowTypes";

// ─── Per-step-type color palette (mirrors workflowUtils nodeTypeOptions) ──────

const STEP_COLORS: Record<string, { dot: string; text: string; bg: string; border: string }> = {
  agent:       { dot: "#B87333", text: "#7a4a1e", bg: "rgba(184,115,51,0.07)",  border: "rgba(184,115,51,0.28)" },
  tool:        { dot: "#789769", text: "#3d5c34", bg: "rgba(120,151,105,0.07)", border: "rgba(120,151,105,0.28)" },
  human_input: { dot: "#8E588E", text: "#5a3060", bg: "rgba(142,88,142,0.07)", border: "rgba(142,88,142,0.28)" },
  conditional: { dot: "#D4A017", text: "#7a5c00", bg: "rgba(212,160,23,0.07)", border: "rgba(212,160,23,0.28)" },
  loop:        { dot: "#4A90D9", text: "#1a4f8a", bg: "rgba(74,144,217,0.07)", border: "rgba(74,144,217,0.28)" },
  merge:       { dot: "#2E8B57", text: "#1a5235", bg: "rgba(46,139,87,0.07)",  border: "rgba(46,139,87,0.28)"  },
  wait:        { dot: "#6B7B8D", text: "#3d4c5a", bg: "rgba(107,123,141,0.07)",border: "rgba(107,123,141,0.28)"},
};

const stepColor = (stepType: string) =>
  STEP_COLORS[stepType] ?? STEP_COLORS["agent"];

const stepIcon = (stepType: string, size = 14): React.ReactElement => {
  const props = { sx: { fontSize: size } };
  switch (stepType) {
    case "tool":        return <BuildIcon {...props} />;
    case "human_input": return <PersonOutlineIcon {...props} />;
    case "conditional": return <AltRouteIcon {...props} />;
    case "loop":        return <LoopIcon {...props} />;
    case "merge":       return <CallMergeIcon {...props} />;
    default:            return <SmartToyIcon {...props} />;
  }
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface DerivedStep {
  id: string;
  stepType: "agent" | "tool" | string;
  label: string;
  status: "queued" | "running" | "complete" | "error" | "paused" | "waiting";
  startedAt?: string | null;
  completedAt?: string | null;
  outputPreview?: string;
  promptPreview?: string;
  errorMessage?: string | null;
  agentVersion?: number;
  agentRunNumber?: number;
  approvalId?: string | null;
  parentLoop?: string | null;
}

export interface WorkflowRunTimelineProps {
  runDetails: WorkflowRunDetails | null;
  workflowName?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso?: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "";
  }
}

function durationLabel(startIso?: string | null, endIso?: string | null): string {
  if (!startIso) return "";
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const secs = Math.round((end - start) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

/**
 * Convert execution_logs.steps dict → DerivedStep array.
 *
 * Rules:
 *  - Started steps (have started_at) → shown with their actual status.
 *  - The single NEXT not-yet-started step (first one after all started steps,
 *    ordered by the events array) → shown as "queued".
 *  - All other not-yet-started steps → hidden.
 *
 * Ordering is derived from the events array when available, otherwise by
 * started_at for started steps, with the queued step appended last.
 */
function deriveSteps(runDetails: WorkflowRunDetails | null): DerivedStep[] {
  if (!runDetails) return [];

  const stepsMap: Record<string, any> =
    (runDetails.execution_logs as any)?.steps ?? {};
  const events: Array<{ event: string; step_id: string; timestamp?: string }> =
    (runDetails.execution_logs as any)?.events ?? [];

  // Build a stable order from the events "started" sequence
  const eventOrder: string[] = [];
  events.forEach((ev) => {
    if (ev.event === "started" && !eventOrder.includes(ev.step_id)) {
      eventOrder.push(ev.step_id);
    }
  });

  // All step keys known to us
  const allKeys = Object.keys(stepsMap);

  // Build a set of terminal loop step IDs so we can override stale body step
  // statuses. A body step showing "waiting_for_approval" after the parent loop
  // finished (success OR failed) is misleading — it must have been resolved
  // for the loop to continue past it.
  const terminalLoopIds = new Set<string>();
  const failedLoopIds = new Set<string>();
  allKeys.forEach((key) => {
    const s = stepsMap[key];
    const raw = (s.status ?? "").toLowerCase();
    if (s.step_type === "loop") {
      if (raw === "success" || raw === "completed") {
        terminalLoopIds.add(key);
      } else if (raw === "failed" || raw === "error") {
        terminalLoopIds.add(key);
        failedLoopIds.add(key);
      }
    }
  });

  // Started steps
  const startedSteps: DerivedStep[] = allKeys
    .filter((key) => stepsMap[key].started_at)
    .map((key) => {
      const s = stepsMap[key];
      const rawStatus: string = (s.status ?? "").toLowerCase();
      const parentLoop: string | null = s.parent_loop ?? null;

      let status: DerivedStep["status"] = "running";
      if (rawStatus === "success" || rawStatus === "completed") {
        status = "complete";
      } else if (rawStatus === "error" || rawStatus === "failed") {
        status = "error";
      } else if (rawStatus === "paused") {
        status = "paused";
      } else if (rawStatus === "waiting_for_approval" || rawStatus === "waiting_for_input") {
        status = "waiting";
      }

      // If this step's parent loop has reached a terminal state, override
      // stale body step statuses. The body step must have been resolved for
      // the loop to have moved past it — mark as complete. (The failure, if
      // any, belongs to the loop step itself or a different body step.)
      if (parentLoop && terminalLoopIds.has(parentLoop) && status !== "error") {
        status = "complete";
      }

      return {
        id: key,
        stepType: s.step_type ?? "agent",
        label: s.step_name ?? key,
        status,
        startedAt: s.started_at,
        completedAt: s.completed_at ?? null,
        outputPreview: s.output_preview,
        promptPreview: s.prompt_preview,
        errorMessage: s.error ?? null,
        agentVersion: s.agent_version,
        agentRunNumber: s.agent_run_number,
        approvalId: s.approval_id ?? null,
        parentLoop,
      };
    });

  // Sort started steps: by eventOrder first, then by started_at
  startedSteps.sort((a, b) => {
    const ai = eventOrder.indexOf(a.id);
    const bi = eventOrder.indexOf(b.id);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    const ta = new Date(a.startedAt ?? 0).getTime();
    const tb = new Date(b.startedAt ?? 0).getTime();
    return ta - tb;
  });

  // Only show a queued step when the workflow is still running (not terminal)
  const isTerminalRun =
    runDetails.status === "completed" || runDetails.status === "failed" ||
    runDetails.status === "cancelled" || runDetails.status === "error";

  if (!isTerminalRun) {
    // Find the first not-yet-started step that isn't already in startedSteps
    const startedIds = new Set(startedSteps.map((s) => s.id));
    const notStartedKeys = allKeys.filter((k) => !startedIds.has(k));

    // Pick the next one: prefer the one that comes next in eventOrder, else first alphabetically
    const nextKey = notStartedKeys.sort((a, b) => {
      const ai = eventOrder.indexOf(a);
      const bi = eventOrder.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      return 0;
    })[0];

    if (nextKey) {
      const s = stepsMap[nextKey];
      startedSteps.push({
        id: nextKey,
        stepType: s.step_type ?? "agent",
        label: s.step_name ?? nextKey,
        status: "queued",
        startedAt: null,
        completedAt: null,
        outputPreview: undefined,
        promptPreview: s.prompt_preview, // may be present even before start
        errorMessage: null,
        agentVersion: s.agent_version,
        agentRunNumber: undefined,
      });
    }
  }

  return startedSteps;
}

// ─── Component ────────────────────────────────────────────────────────────────

const WorkflowRunTimeline: React.FC<WorkflowRunTimelineProps> = ({
  runDetails,
  workflowName,
}) => {
  const errorRed  = "#d32f2f";
  const queuedGray = "#94a3b8";

  const steps = useMemo(() => deriveSteps(runDetails), [runDetails]);

  // ── Progress: use TOTAL known steps as denominator, not just started ones ──
  const totalStepsInRun: number = Object.keys(
    (runDetails?.execution_logs as any)?.steps ?? {}
  ).length;
  const completedCount = steps.filter((s) => s.status === "complete").length;
  const overallStatus  = runDetails?.status ?? null;
  const isTerminal     =
    overallStatus === "completed" || overallStatus === "failed" ||
    overallStatus === "cancelled" || overallStatus === "error";

  // Any terminal state → 100% fill. The bar color already distinguishes
  // success (green) from failure (red). While running → indeterminate animation.
  const denom   = totalStepsInRun > 0 ? totalStepsInRun : steps.length;
  const progress = isTerminal
    ? 100
    : denom === 0 ? 0 : Math.round((completedCount / denom) * 100);

  // ── Empty / no-run state ───────────────────────────────────────────────────
  if (!runDetails) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2, color: "text.secondary" }}>
        <PsychologyIcon sx={{ fontSize: 48, opacity: 0.25 }} />
        <Typography variant="body2">Select a run to view its execution timeline.</Typography>
      </Box>
    );
  }

  if (steps.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2, color: "text.secondary" }}>
        <CircularProgress size={28} thickness={4} sx={{ color: "#4F6AF0" }} />
        <Typography variant="body2">Waiting for steps to start…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mx: 2, mt: 2, px: 2, pt: 1.5, pb: 2, overflowY: "auto", height: "100%" }}>

      {/* ── Full-width header with text label + progress bar ──────────────── */}
      <Box
        sx={{
          mb: 2,
          // px: 2,
          py: 1,
          background: "rgba(255,255,255,0.6)",
          backdropFilter: "blur(8px)",
        }}
      >
        {/* Top row: title + step count */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: "#B87333", display: "flex", alignItems: "center", gap: 0.75 }}
          >
            <PsychologyIcon sx={{ fontSize: 16 }} />
            {workflowName ? `${workflowName} — Execution` : "Execution Timeline"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#4F6AF0", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {completedCount} / {denom > 0 ? denom : "?"} steps
            {overallStatus && (
              <span style={{ marginLeft: 8, textTransform: "capitalize", opacity: 0.65, fontWeight: 500 }}>
                · {overallStatus}
              </span>
            )}
          </Typography>
        </Box>
        {/* Progress bar */}
        <LinearProgress
          variant={isTerminal ? "determinate" : "indeterminate"}
          value={isTerminal ? progress : undefined}
          sx={{
            height: 7,
            borderRadius: 4,
            bgcolor: "rgba(79,106,240,0.1)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              background:
                overallStatus === "failed" || overallStatus === "error"
                  ? `linear-gradient(90deg, ${errorRed} 0%, #ff6b6b 100%)`
                  : overallStatus === "completed"
                  ? "linear-gradient(90deg, #2E8B57 0%, #52c282 100%)"
                  : "linear-gradient(90deg, #4F6AF0 0%, #818CF8 100%)",
            },
            "& .MuiLinearProgress-bar1Indeterminate": {
              background: "linear-gradient(90deg, #4F6AF0 0%, #818CF8 100%)",
            },
            "& .MuiLinearProgress-bar2Indeterminate": {
              background: "linear-gradient(90deg, #818CF8 0%, #4F6AF0 100%)",
            },
          }}
        />
      </Box>

      {/* ── Timeline — no opposite content; timestamp lives inside card ──── */}
      <Timeline position="right" sx={{ p: 0, m: 0,  }}>
        {steps.map((step, index) => {
          const isRunning = step.status === "running";
          const isError   = step.status === "error";
          const isQueued  = step.status === "queued";
          const isDone    = step.status === "complete";
          const isPaused  = step.status === "paused";
          const isWaiting = step.status === "waiting";
          const isPausedOrWaiting = isPaused || isWaiting;

          const colors = stepColor(step.stepType);
          const pausedAmber = "#E67E22";

          const activeDotBg     = isError ? errorRed : isRunning ? "white" : isPausedOrWaiting ? "white" : isQueued ? "white" : colors.dot;
          const activeDotBorder = isError ? errorRed : isRunning ? colors.dot : isPausedOrWaiting ? pausedAmber : isQueued ? queuedGray : colors.dot;
          const activeCardBg    = isError ? "rgba(211,47,47,0.05)" : isPausedOrWaiting ? "rgba(230,126,34,0.06)" : isQueued ? "rgba(148,163,184,0.05)" : colors.bg;
          const activeCardBorder= isError ? "rgba(211,47,47,0.25)" : isPausedOrWaiting ? "rgba(230,126,34,0.3)" : isQueued ? "rgba(148,163,184,0.3)" : colors.border;
          const connectorColor  = isQueued ? "transparent" : `${colors.dot}33`;

          const hasDetails = !isQueued && (step.promptPreview || step.outputPreview || isError);

          return (
            <TimelineItem key={step.id} sx={{ minHeight: 0, "&:before": { display: "none" } }}>

              <TimelineSeparator>
                {/* Top connector — always flex:1; invisible on first item so the line doesn't start above it */}
                <TimelineConnector
                  sx={{
                    width: "2px",
                    ...(index === 0
                      ? { bgcolor: "transparent" }
                      : isQueued
                      ? { bgcolor: "transparent", borderLeft: `2px dashed ${queuedGray}` }
                      : { bgcolor: connectorColor }),
                  }}
                />
                <TimelineDot
                  sx={{
                    p: "5px",
                    background: activeDotBg,
                    border: `2px ${isQueued ? "dashed" : "solid"} ${activeDotBorder}`,
                    boxShadow: isRunning ? `0 0 8px ${colors.dot}88` : "none",
                    opacity: isQueued ? 0.6 : 1,
                    transition: "all 0.25s ease",
                    my: "6px",
                    flexShrink: 0,
                  }}
                >
                  {isRunning ? (
                    <CircularProgress size={13} thickness={5} sx={{ color: colors.dot }} />
                  ) : isError ? (
                    <ErrorOutlineIcon sx={{ fontSize: 14, color: "white" }} />
                  ) : isPausedOrWaiting ? (
                    <PauseCircleOutlineIcon sx={{ fontSize: 14, color: pausedAmber }} />
                  ) : isQueued ? (
                    <HourglassEmptyIcon sx={{ fontSize: 14, color: queuedGray }} />
                  ) : (
                    <Box sx={{ fontSize: 14, color: isDone ? "white" : colors.dot, display: "flex" }}>
                      {stepIcon(step.stepType, 14)}
                    </Box>
                  )}
                </TimelineDot>
                {/* Bottom connector — always flex:1; invisible on last item so line doesn't trail off */}
                <TimelineConnector
                  sx={{
                    width: "2px",
                    ...(index === steps.length - 1
                      ? { bgcolor: "transparent" }
                      : steps[index + 1]?.status === "queued"
                      ? { bgcolor: "transparent", borderLeft: `2px dashed ${queuedGray}` }
                      : { bgcolor: connectorColor }),
                  }}
                />
              </TimelineSeparator>

              {/* Step card */}
              <TimelineContent sx={{ py: "8px", pl: 1.5, pr: 0 }}>
                <Accordion
                  elevation={0}
                  defaultExpanded={false}
                  disabled={isQueued || !hasDetails}
                  disableGutters
                  sx={{
                    background: activeCardBg,
                    border: "1px solid",
                    borderStyle: isQueued ? "dashed" : "solid",
                    borderColor: activeCardBorder,
                    borderRadius: "10px !important",
                    opacity: isQueued ? 0.65 : 1,
                    transition: "all 0.15s ease",
                    "&:hover": isQueued ? {} : { transform: "translateX(2px)" },
                    "&:before": { display: "none" },
                    "&.Mui-disabled": { opacity: isQueued ? 0.65 : 1, bgcolor: "transparent" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={hasDetails ? <ExpandMoreIcon sx={{ fontSize: 16, color: colors.dot }} /> : null}
                    sx={{
                      minHeight: "44px",
                      py: 0,
                      px: 1.5,
                      cursor: hasDetails ? "pointer" : "default !important",
                      "& .MuiAccordionSummary-content": { my: "10px", alignItems: "center", gap: 1 },
                    }}
                  >
                    {/* Step type icon */}
                    <Box sx={{ fontSize: 15, color: isQueued ? queuedGray : isError ? errorRed : colors.dot, flexShrink: 0, display: "flex" }}>
                      {stepIcon(step.stepType, 15)}
                    </Box>

                    {/* Label + step type sub-line */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.82rem",
                          color: isError ? errorRed : isQueued ? "#64748b" : colors.text,
                          lineHeight: 1.3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {step.label}
                        {step.agentVersion != null && (
                          <span style={{ fontWeight: 400, opacity: 0.5, marginLeft: 5, fontSize: "0.72rem" }}>
                            v{step.agentVersion}
                          </span>
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: "0.68rem", color: "text.disabled", lineHeight: 1, textTransform: "capitalize" }}>
                        {step.stepType.replace("_", " ")}
                      </Typography>
                    </Box>

                    {/* Right: timestamp + status badge */}
                    <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
                      {/* Timestamp */}
                      <Typography variant="caption" sx={{ fontSize: "0.67rem", color: "text.disabled", fontVariantNumeric: "tabular-nums" }}>
                        {isQueued ? <em style={{ color: queuedGray }}>next</em> : formatTime(step.startedAt)}
                      </Typography>
                      {/* Status */}
                      {isRunning && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                          <CircularProgress size={9} thickness={5} sx={{ color: colors.dot }} />
                          <Typography variant="caption" sx={{ fontSize: "0.67rem", color: colors.dot, fontWeight: 600 }}>running</Typography>
                        </Box>
                      )}
                      {isWaiting && (
                        <Typography variant="caption" sx={{ fontSize: "0.67rem", color: pausedAmber, fontWeight: 600 }}>
                          waiting for review
                        </Typography>
                      )}
                      {isPaused && (
                        <Typography variant="caption" sx={{ fontSize: "0.67rem", color: pausedAmber, fontWeight: 600 }}>
                          paused
                        </Typography>
                      )}
                      {isDone && (
                        <Typography variant="caption" sx={{ fontSize: "0.67rem", color: colors.dot, opacity: 0.8 }}>
                          {durationLabel(step.startedAt, step.completedAt)}
                        </Typography>
                      )}
                      {isError && (
                        <Typography variant="caption" sx={{ fontSize: "0.67rem", color: errorRed, fontWeight: 600 }}>failed</Typography>
                      )}
                    </Box>
                  </AccordionSummary>

                  {/* Expandable details */}
                  {hasDetails && (
                    <AccordionDetails
                      sx={{
                        bgcolor: "rgba(0,0,0,0.025)",
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                        borderBottomLeftRadius: "10px",
                        borderBottomRightRadius: "10px",
                        p: 1.25,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {step.promptPreview && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#888", display: "block", mb: 0.25 }}>Prompt</Typography>
                          <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "0.70rem", color: "#555", bgcolor: "rgba(0,0,0,0.03)", p: 0.75, borderRadius: "5px", maxHeight: 100, overflowY: "auto", m: 0 }}>
                            {step.promptPreview}
                          </Typography>
                        </Box>
                      )}
                      {step.outputPreview && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#888", display: "block", mb: 0.25 }}>Output</Typography>
                          <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "0.70rem", color: "#334155", bgcolor: `${colors.dot}0d`, p: 0.75, borderRadius: "5px", maxHeight: 140, overflowY: "auto", m: 0 }}>
                            {step.outputPreview}
                          </Typography>
                        </Box>
                      )}
                      {isError && (
                        <Typography variant="caption" sx={{ color: errorRed, display: "flex", alignItems: "center", gap: 0.5 }}>
                          <ErrorOutlineIcon sx={{ fontSize: 13 }} />
                          {step.errorMessage ?? "This step failed."}
                        </Typography>
                      )}
                    </AccordionDetails>
                  )}
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Box>
  );
};

export default WorkflowRunTimeline;
